// Wave 3.39 — Order Form Material Transfer dialog.
// Moves raw material between two Order Forms belonging to the **same**
// Goldsmith. Source order ledger records a "transfer_out" (Returned-style)
// entry; destination order records a "transfer_in" (Issued-style) entry.
// Both entries share a transfer_id so the audit trail is bi-directional.
import React, { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { formatINR, formatCarats, formatGrams } from '@/lib/format';
// Wave 3.49 — portal-wide carat/piece validation (Rule 1 + Rule 2).
import { validateCaratMovement, validateMovementQty } from '@/lib/caratValidation';
import { ArrowRightLeft, Gem, Sparkles, Circle, Coins, AlertCircle, Loader2 } from 'lucide-react';

const MATERIAL_META = {
  diamond: { label: 'Diamond', icon: Gem, color: 'text-[var(--diamond-icon)]' },
  color_stone: { label: 'Colour Stone', icon: Sparkles, color: 'text-pink-500' },
  pearl: { label: 'Pearl', icon: Circle, color: 'text-slate-500' },
  gold: { label: 'Gold', icon: Coins, color: 'text-amber-600' },
};

export function TransferMaterialDialog({ open, onOpenChange, sourceOrder, onTransferred }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  // Wave 3.42 — Transfer mode: 'order' = to another order form (legacy),
  // 'unattributed' = back to the goldsmith's Unattributed pool (no dest order).
  const [transferMode, setTransferMode] = useState('order');
  const [destId, setDestId] = useState('');
  const [pickedKey, setPickedKey] = useState('');
  // Quantity inputs (we use separate fields per material kind)
  const [carats, setCarats] = useState('');
  const [pieces, setPieces] = useState('');
  const [grams, setGrams] = useState('');
  const [note, setNote] = useState('');
  // Wave 4.12 — per-issue-entry allocations for multi-entry carat packets:
  // { [line_id]: { carats, pieces } }. Mirrors the Return flow's entry table.
  const [entryAlloc, setEntryAlloc] = useState({});
  // Wave 3.39 (extension) — optional requirement attribution on the dest order.
  const [destReqs, setDestReqs] = useState([]);
  const [destReqsLoading, setDestReqsLoading] = useState(false);
  const [destReqId, setDestReqId] = useState(''); // '' === Unattributed

  // Reset state whenever the dialog re-opens
  useEffect(() => {
    if (open) {
      setTransferMode('order');
      setDestId('');
      setPickedKey('');
      setCarats('');
      setPieces('');
      setGrams('');
      setNote('');
      setEntryAlloc({});
      setData(null);
      setDestReqs([]);
      setDestReqId('');
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadData = async () => {
    if (!sourceOrder?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/goldsmith-issues/transferable-balances/${sourceOrder.id}`);
      setData(data);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load transferable balances'));
    } finally {
      setLoading(false);
    }
  };

  const balances = data?.balances || [];
  const eligibleDestinations = data?.eligible_destinations || [];

  // Build a stable selector key per balance row.
  const keyFor = (b) => (b.material_type === 'gold' ? `gold:${b.purity_id || ''}` : `${b.material_type}:${b.packet_id || ''}`);
  const picked = useMemo(() => balances.find((b) => keyFor(b) === pickedKey) || null, [balances, pickedKey]);

  // Wave 4.12 — Per-issue-entry transfer. When the picked carat packet has
  // MORE THAN ONE source issue entry, switch to a per-entry table (mirrors
  // the Return flow) so the user transfers against the exact source rows and
  // each row's own note carries forward to the destination.
  const sourceLines = useMemo(() => (picked?.lines || []), [picked]);
  // Wave 4.13b — Only issue entries that still carry a LIVE balance are
  // transferable. The backend already nets returns + prior transfers into
  // each line's per-order balance (and omits fully-depleted lines); we guard
  // here too so an entry with no remaining balance (both carats AND pieces
  // at zero) never renders in the per-entry table.
  const transferableLines = useMemo(
    () => sourceLines.filter(
      (ln) => (Number(ln.carats) || 0) > 1e-9 || (Number(ln.pieces) || 0) > 0,
    ),
    [sourceLines],
  );
  const entryMode = !!(picked && picked.material_type !== 'gold' && transferableLines.length > 1);
  const updateEntryAlloc = (lineId, patch) => setEntryAlloc((prev) => ({
    ...prev, [lineId]: { ...(prev[lineId] || {}), ...patch },
  }));
  const entryRows = useMemo(() => {
    if (!entryMode) return [];
    return transferableLines.map((ln) => {
      const a = entryAlloc[ln.line_id] || {};
      const ct = Number(a.carats || 0);
      const pcs = Number(a.pieces || 0);
      const availCt = ln.carats || 0;
      const availPcs = ln.pieces || 0;
      const over = (ct - availCt > 1e-9) || (pcs > availPcs);
      const hasQty = ct > 0 || pcs > 0;
      const ruleViol = hasQty && !!validateMovementQty(ct, pcs, { context: 'issue-entry transfer' });
      // Rule 2 — the entry's remainder after transfer must stay consistent
      // (both > 0, or both 0). Blocks leaving a degenerate "pcs / 0 ct" row.
      const remCt = availCt - ct;
      const remPcs = availPcs - pcs;
      const consistent = (remCt > 1e-9 && remPcs > 0) || (Math.abs(remCt) <= 1e-9 && remPcs === 0);
      const orphan = hasQty && !over && !consistent;
      return { ...ln, ct, pcs, availCt, availPcs, over, hasQty, ruleViol, orphan };
    });
  }, [entryMode, transferableLines, entryAlloc]);
  const entryTotals = useMemo(() => {
    let ct = 0, pcs = 0, anyOver = false, anyRule = false, anyQty = false, anyOrphan = false;
    for (const r of entryRows) {
      if (r.hasQty) { ct += r.ct; pcs += r.pcs; anyQty = true; }
      if (r.over) anyOver = true;
      if (r.ruleViol) anyRule = true;
      if (r.orphan) anyOrphan = true;
    }
    return { ct: Math.round(ct * 100) / 100, pcs, anyOver, anyRule, anyQty, anyOrphan };
  }, [entryRows]);

  // Wave 3.42 — Clear destination order + requirement whenever the user
  // switches transfer mode so stale selections don't leak across radios.
  useEffect(() => {
    if (transferMode === 'unattributed') {
      setDestId('');
      setDestReqs([]);
      setDestReqId('');
    }
  }, [transferMode]);

  // Wave 3.42 — If the source goldsmith has no other active order, the only
  // viable transfer destination IS the unattributed pool — auto-flip to that
  // mode the moment data lands so the dialog is usable.
  useEffect(() => {
    if (data && (data.eligible_destinations || []).length === 0) {
      setTransferMode('unattributed');
    }
  }, [data]);

  // Wave 3.39 (extension) — Whenever the destination order OR the picked
  // balance changes, refetch the dest order's pending requirements
  // filtered down to the matching material + classification (or purity).
  useEffect(() => {
    if (transferMode === 'unattributed' || !destId || !picked) {
      setDestReqs([]);
      setDestReqId('');
      return;
    }
    let cancelled = false;
    const fetchReqs = async () => {
      setDestReqsLoading(true);
      try {
        const params = { material_type: picked.material_type };
        if (picked.material_type === 'gold') {
          if (picked.purity_id) params.purity_id = picked.purity_id;
        } else if (picked.classification_id) {
          params.classification_id = picked.classification_id;
        }
        const { data } = await api.get(`/orders/${destId}/pending-requirements`, { params });
        if (!cancelled) {
          setDestReqs(data?.requirements || []);
          setDestReqId(''); // reset selection on every refetch
        }
      } catch (e) {
        if (!cancelled) {
          setDestReqs([]);
          setDestReqId('');
        }
      } finally {
        if (!cancelled) setDestReqsLoading(false);
      }
    };
    fetchReqs();
    return () => {
      cancelled = true;
    };
  }, [destId, picked, transferMode]);

  const submit = async () => {
    if (!picked) {
      toast.error('Pick what to transfer.');
      return;
    }
    if (transferMode === 'order' && !destId) {
      toast.error('Pick which order to transfer to.');
      return;
    }
    const toUnattributed = transferMode === 'unattributed';
    const payload = {
      source_order_id: sourceOrder.id,
      material_type: picked.material_type,
      note: (note || '').trim(),
      // Wave 3.42 — explicit flag so the backend can split unattributed
      // transfers from order-to-order ones.
      to_unattributed: toUnattributed,
    };
    if (!toUnattributed) {
      payload.dest_order_id = destId;
      // Wave 3.39 (extension) — null means "attribute to Unattributed pool".
      payload.dest_requirement_id = destReqId || null;
    }
    if (picked.material_type === 'gold') {
      const g = parseFloat(grams);
      if (!(g > 0)) {
        toast.error('Enter grams to transfer.');
        return;
      }
      if (g - picked.grams > 1e-6) {
        toast.error(`Cannot transfer more than the available ${picked.grams.toFixed(3)} g.`);
        return;
      }
      payload.purity_id = picked.purity_id || null;
      payload.grams = g;
    } else if (entryMode) {
      // Wave 4.12 — per-issue-entry carat transfer. Send one allocation per
      // filled source entry; the backend moves material against the exact
      // (issue_id, line_id) and carries each entry's note forward.
      if (!entryTotals.anyQty) {
        toast.error('Enter a transfer quantity on at least one issue entry.');
        return;
      }
      if (entryTotals.anyOver) {
        toast.error('One or more entries exceed their available balance.');
        return;
      }
      if (entryTotals.anyRule) {
        toast.error('Each entry you transfer must have both carats > 0 and pieces > 0.');
        return;
      }
      if (entryTotals.anyOrphan) {
        toast.error('A transfer would leave an entry with pieces but no carats (or vice-versa). Adjust the quantities.');
        return;
      }
      payload.packet_id = picked.packet_id;
      payload.entries = entryRows
        .filter((r) => r.hasQty)
        .map((r) => ({ issue_id: r.issue_id, line_id: r.line_id, carats: r.ct, pieces: r.pcs }));
      payload.carats = entryTotals.ct;
      payload.pieces = entryTotals.pcs;
    } else {
      const ct = parseFloat(carats || '0') || 0;
      const pcs = parseInt(pieces || '0', 10) || 0;
      // Wave 3.49 Rule 1 + Rule 2 — both qty > 0 and source order's
      // post-transfer attribution remains consistent.
      const ruleErr = validateCaratMovement({
        txnCarats: ct, txnPieces: pcs,
        availableCarats: picked.carats || 0,
        availablePieces: picked.pieces || 0,
        packetNo: picked.packet_no || '',
        context: 'order-form material transfer',
      });
      if (ruleErr) { toast.error(ruleErr); return; }
      payload.packet_id = picked.packet_id;
      payload.carats = ct;
      payload.pieces = pcs;
    }
    setSubmitting(true);
    try {
      await api.post('/goldsmith-issues/transfer-material', payload);
      const destLabel = toUnattributed
        ? `${data?.goldsmith_name || 'goldsmith'}'s Unattributed Pool`
        : (eligibleDestinations.find((o) => o.id === destId)?.order_no || 'destination');
      toast.success(`Material transferred to ${destLabel}`);
      onOpenChange(false);
      if (onTransferred) onTransferred();
    } catch (e) {
      toast.error(extractError(e, 'Transfer failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="transfer-material-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft size={18} className="text-[var(--ink-navy)]" />
            Transfer Material
          </DialogTitle>
          <DialogDescription>
            Move a portion of the raw material currently on this order over to <b>another order form of the same goldsmith</b> ({data?.goldsmith_name || sourceOrder?.goldsmith_name || '—'}) — or back to the goldsmith&apos;s <b>Unattributed Pool</b>. Either way the source order&apos;s ledger records a Return-style entry and the goldsmith&apos;s overall balance stays unchanged.
          </DialogDescription>
        </DialogHeader>

        {/* Wave 4.13b — The body scrolls independently so large packets (many
            issue entries) stay fully reachable while the header + the
            Transfer action footer remain pinned. */}
        <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1" data-testid="transfer-material-body">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500" data-testid="transfer-material-loading">
            <Loader2 size={14} className="animate-spin" />
            Loading transferable balances…
          </div>
        ) : balances.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6 text-sm text-slate-500 border border-dashed rounded-md" data-testid="transfer-material-empty">
            <AlertCircle size={20} className="text-amber-500" />
            No material currently held by this order — nothing to transfer.
          </div>
        ) : (
          <div className="space-y-4">
            {/* 0) Transfer mode — Wave 3.42 */}
            <div data-testid="transfer-mode-section">
              <Label className="text-sm font-medium">Transfer to</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTransferMode('order')}
                  disabled={eligibleDestinations.length === 0}
                  data-testid="transfer-mode-order"
                  className={`px-3 py-2 rounded-md border text-left text-sm transition ${
                    transferMode === 'order'
                      ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white'
                      : eligibleDestinations.length === 0
                        ? 'border-[var(--divider)] text-slate-400 cursor-not-allowed'
                        : 'border-[var(--divider)] hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2 font-medium">
                    <ArrowRightLeft size={13} /> Another Order Form
                  </div>
                  <div className={`text-[11px] mt-0.5 ${transferMode === 'order' ? 'text-white/80' : 'text-slate-500'}`}>
                    {eligibleDestinations.length === 0
                      ? 'No other active order for this goldsmith'
                      : `${eligibleDestinations.length} eligible order${eligibleDestinations.length === 1 ? '' : 's'} available`}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setTransferMode('unattributed')}
                  data-testid="transfer-mode-unattributed"
                  className={`px-3 py-2 rounded-md border text-left text-sm transition ${
                    transferMode === 'unattributed'
                      ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white'
                      : 'border-[var(--divider)] hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2 font-medium">
                    <Coins size={13} /> Unattributed Pool
                  </div>
                  <div className={`text-[11px] mt-0.5 ${transferMode === 'unattributed' ? 'text-white/80' : 'text-slate-500'}`}>
                    Stays with this goldsmith, free for any future order
                  </div>
                </button>
              </div>
            </div>

            {/* 1) Pick what to transfer */}
            <div>
              <Label className="text-sm font-medium">1. What to transfer</Label>
              <div className="mt-2 max-h-56 overflow-y-auto border border-[var(--divider)] rounded-md" data-testid="transfer-material-balance-list">
                {balances.map((b) => {
                  const k = keyFor(b);
                  const meta = MATERIAL_META[b.material_type] || MATERIAL_META.diamond;
                  const Icon = meta.icon;
                  const selected = pickedKey === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => {
                        setPickedKey(k);
                        setEntryAlloc({});
                        // Pre-fill the maximum so user only needs to override
                        if (b.material_type === 'gold') {
                          setGrams(String(b.grams));
                          setCarats('');
                          setPieces('');
                        } else {
                          setCarats(String(b.carats));
                          setPieces(String(b.pieces));
                          setGrams('');
                        }
                      }}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm border-b last:border-b-0 border-[var(--divider)] transition ${selected ? 'bg-emerald-50 ring-1 ring-emerald-300' : 'hover:bg-slate-50'}`}
                      data-testid={`transfer-material-balance-${k}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={14} className={meta.color} />
                        <span className="font-medium">{meta.label}</span>
                        <span className="font-mono text-xs text-slate-600">
                          {b.material_type === 'gold' ? (b.purity_name || '—') : (b.packet_no || '—')}
                        </span>
                      </div>
                      <div className="font-mono tabular-nums text-xs text-slate-700">
                        {b.material_type === 'gold' ? (
                          <span>{formatGrams(b.grams)} g <span className="text-slate-400">({formatGrams(b.grams_999)} g999)</span></span>
                        ) : (
                          <span>{formatCarats(b.carats)} ct · {b.pieces} pcs <span className="text-slate-400">· {formatINR(b.value)}</span></span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 2) Destination order — only when transferMode === 'order' */}
            {transferMode === 'order' && (
              <div>
                <Label className="text-sm font-medium" htmlFor="transfer-dest-select">2. Destination order (same goldsmith)</Label>
                <div className="mt-2">
                  <SearchableSelect
                    items={eligibleDestinations}
                    value={destId}
                    onValueChange={(v) => setDestId(v || '')}
                    itemToLabel={(o) => (
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-xs">{o.order_no}</span>
                        <span className="truncate text-slate-700">{o.title || ''}</span>
                        {o.hold_status === 'on_hold' && <Badge variant="secondary" className="text-[10px]">On Hold</Badge>}
                      </span>
                    )}
                    searchKeys={['order_no', 'title']}
                    placeholder={eligibleDestinations.length === 0 ? 'No other active order — switch to Unattributed Pool' : 'Pick another Order Form…'}
                    searchPlaceholder="Search order no / title…"
                    disabled={!pickedKey || eligibleDestinations.length === 0}
                    testId="transfer-dest-select"
                  />
                </div>
              </div>
            )}

            {/* Unattributed pool — explanatory tile in lieu of the dest order picker */}
            {transferMode === 'unattributed' && picked && (
              <div
                className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-[12px] text-amber-900"
                data-testid="transfer-mode-unattributed-info"
              >
                The transferred material will return to <b>{data?.goldsmith_name || 'this goldsmith'}&apos;s Unattributed Pool</b> — it stays physically with the goldsmith and can be attributed to a future order at any time.
              </div>
            )}

            {/* 3) Attribute to a pending requirement (optional) — only when going to another order */}
            {transferMode === 'order' && picked && destId && (
              <div data-testid="transfer-requirement-section">
                <Label className="text-sm font-medium" htmlFor="transfer-req-select">
                  3. Attribute to a pending requirement <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                {destReqsLoading ? (
                  <div className="flex items-center gap-2 mt-2 text-xs text-slate-500" data-testid="transfer-requirement-loading">
                    <Loader2 size={12} className="animate-spin" />
                    Loading pending requirements…
                  </div>
                ) : destReqs.length === 0 ? (
                  <div
                    className="mt-2 px-3 py-2.5 rounded-md border border-dashed border-[var(--divider)] bg-slate-50/60 text-[11px] text-slate-500"
                    data-testid="transfer-requirement-empty"
                  >
                    No matching pending requirement on the destination order. The transfer will land in the <b>Unattributed Pool</b>.
                  </div>
                ) : (
                  <>
                    {(() => {
                      const reqItems = destReqs.map((r) => {
                        const isGold = picked.material_type === 'gold';
                        const tail = isGold
                          ? `Remaining ${formatGrams(r.remaining_grams)} g`
                          : `Remaining ${formatCarats(r.remaining_carats)} ct / ${r.remaining_pieces} pcs`;
                        const desc = r.description ? ` — ${r.description}` : '';
                        const ageBits = r.days_since_requested > 0 ? ` · ${r.days_since_requested}d old` : '';
                        return {
                          id: r.id,
                          label: `${r.classification_label || 'Requirement'}${desc}`,
                          sublabel: `${tail}${ageBits}`,
                        };
                      });
                      const items = [{ id: '__unattributed__', label: 'Leave in Unattributed Pool (default)', sublabel: '' }, ...reqItems];
                      return (
                        <div className="mt-2">
                          <SearchableSelect
                            items={items}
                            value={destReqId || '__unattributed__'}
                            onValueChange={(v) => setDestReqId(v === '__unattributed__' ? '' : (v || ''))}
                            itemToValue={(it) => it.id}
                            itemToLabel={(it) => it.label}
                            itemToSubLabel={(it) => it.sublabel || null}
                            searchKeys={['label', 'sublabel']}
                            placeholder="Leave in Unattributed Pool…"
                            searchPlaceholder="Search requirement…"
                            testId="transfer-req-select"
                          />
                        </div>
                      );
                    })()}
                    {destReqId && (() => {
                      const r = destReqs.find((x) => x.id === destReqId);
                      if (!r) return null;
                      return (
                        <div className="mt-2 px-3 py-2 rounded-md bg-emerald-50/70 border border-emerald-200 text-[11px] text-emerald-900" data-testid="transfer-requirement-selected">
                          <div className="font-medium text-emerald-900">Attributing to: {r.classification_label || 'Requirement'}</div>
                          <div className="font-mono tabular-nums">
                            {picked.material_type === 'gold' ? (
                              <>Remaining on this requirement: <b>{formatGrams(r.remaining_grams)} g</b></>
                            ) : (
                              <>Remaining on this requirement: <b>{formatCarats(r.remaining_carats)} ct / {r.remaining_pieces} pcs</b></>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {/* 4) Quantity */}
            {picked && (
              <div>
                <Label className="text-sm font-medium">4. Quantity to transfer</Label>
                {picked.material_type === 'gold' ? (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <Label htmlFor="transfer-grams" className="text-xs text-slate-500">Grams (at issued purity)</Label>
                      <Input
                        id="transfer-grams"
                        type="number"
                        step="0.001"
                        min="0"
                        max={picked.grams}
                        value={grams}
                        onChange={(e) => setGrams(e.target.value)}
                        data-testid="transfer-grams-input"
                      />
                      <div className="text-[10px] text-slate-500 mt-0.5">Max {formatGrams(picked.grams)} g</div>
                    </div>
                  </div>
                ) : entryMode ? (
                  <div className="mt-2 rounded-md border border-indigo-200 bg-indigo-50/30 p-2.5" data-testid="transfer-entry-table">
                    <div className="mb-1.5 flex items-center gap-2 text-[12px] font-medium text-indigo-900">
                      <Gem size={13} className="text-indigo-700" />
                      Issue entries for {picked.packet_no || 'this packet'}
                      <Badge variant="outline" className="text-[10px] text-indigo-700 border-indigo-300">{entryRows.length} entries</Badge>
                    </div>
                    <div className="text-[11px] text-indigo-800 mb-2">
                      This packet has multiple issue entries — enter how much to transfer against each. Each entry&apos;s own note carries forward to the destination.
                    </div>
                    <div className="overflow-x-auto overflow-y-auto max-h-[42vh] rounded border border-indigo-100">
                      <table className="w-full text-[11px]">
                        <thead className="sticky top-0 z-10 bg-indigo-50 shadow-[0_1px_0_0_var(--divider)]">
                          <tr className="text-left text-slate-500">
                            <th className="px-2 py-1.5 font-medium whitespace-nowrap">Issue Memo</th>
                            <th className="px-2 py-1.5 font-medium">Note</th>
                            <th className="px-2 py-1.5 font-medium text-right whitespace-nowrap">Available</th>
                            <th className="px-2 py-1.5 font-medium text-right">Transfer ct</th>
                            <th className="px-2 py-1.5 font-medium text-right">Transfer pcs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entryRows.map((ln, li) => (
                            <tr key={ln.line_id} className={`border-t border-indigo-100 ${ln.over || ln.ruleViol || ln.orphan ? 'bg-rose-50/60' : ''}`} data-testid={`transfer-entry-${li}`}>
                              <td className="px-2 py-1 font-mono text-slate-700 whitespace-nowrap align-top">{ln.issue_memo_no || ln.issue_no || '—'}</td>
                              <td className="px-2 py-1 align-top text-slate-700">{ln.note ? ln.note : <span className="text-slate-400">—</span>}</td>
                              <td className="px-2 py-1 text-right font-mono text-slate-600 whitespace-nowrap align-top">{formatCarats(ln.availCt)} ct · {ln.availPcs} pcs</td>
                              <td className="px-2 py-1 text-right align-top">
                                <Input
                                  className="h-8 w-20 ml-auto text-right" type="number" step="0.01" min="0"
                                  value={(entryAlloc[ln.line_id]?.carats) ?? ''}
                                  onChange={(e) => updateEntryAlloc(ln.line_id, { carats: e.target.value })}
                                  placeholder={`${formatCarats(ln.availCt)}`}
                                  data-testid={`transfer-entry-carats-${li}`}
                                />
                              </td>
                              <td className="px-2 py-1 text-right align-top">
                                <Input
                                  className="h-8 w-16 ml-auto text-right" type="number" step="1" min="0"
                                  value={(entryAlloc[ln.line_id]?.pieces) ?? ''}
                                  onChange={(e) => updateEntryAlloc(ln.line_id, { pieces: e.target.value })}
                                  placeholder={`${ln.availPcs}`}
                                  data-testid={`transfer-entry-pieces-${li}`}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 flex-wrap text-[11px]">
                      <span className="text-slate-600">Total to transfer: <span className="font-mono font-semibold text-slate-800">{formatCarats(entryTotals.ct)} ct · {entryTotals.pcs} pcs</span></span>
                      {entryTotals.anyRule && <span className="text-rose-600">Each filled entry needs both carats &gt; 0 and pieces &gt; 0.</span>}
                      {entryTotals.anyOver && <span className="text-rose-600">Exceeds an entry&apos;s available balance.</span>}
                      {entryTotals.anyOrphan && <span className="text-rose-600">Would leave an entry with pieces but no carats (or vice-versa).</span>}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <Label htmlFor="transfer-carats" className="text-xs text-slate-500">Carats</Label>
                      <Input
                        id="transfer-carats"
                        type="number"
                        step="0.01"
                        min="0"
                        max={picked.carats}
                        value={carats}
                        onChange={(e) => setCarats(e.target.value)}
                        data-testid="transfer-carats-input"
                      />
                      <div className="text-[10px] text-slate-500 mt-0.5">Max {formatCarats(picked.carats)} ct</div>
                    </div>
                    <div>
                      <Label htmlFor="transfer-pieces" className="text-xs text-slate-500">Pieces</Label>
                      <Input
                        id="transfer-pieces"
                        type="number"
                        step="1"
                        min="0"
                        max={picked.pieces}
                        value={pieces}
                        onChange={(e) => setPieces(e.target.value)}
                        data-testid="transfer-pieces-input"
                      />
                      <div className="text-[10px] text-slate-500 mt-0.5">Max {picked.pieces} pcs</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 5) Optional note */}
            {picked && (
              <div>
                <Label htmlFor="transfer-note" className="text-sm font-medium">5. Note (optional)</Label>
                <Input
                  id="transfer-note"
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. shifting balance for revised design"
                  data-testid="transfer-note-input"
                  maxLength={200}
                />
              </div>
            )}
          </div>
        )}
        </div>

        <DialogFooter className="mt-2 shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="transfer-material-cancel">Cancel</Button>
          <Button
            className="luxury-button"
            disabled={submitting || loading || !picked || (transferMode === 'order' && !destId) || (entryMode && (!entryTotals.anyQty || entryTotals.anyOver || entryTotals.anyRule || entryTotals.anyOrphan))}
            onClick={submit}
            data-testid="transfer-material-submit"
          >
            {submitting ? (
              <><Loader2 size={14} className="mr-1.5 animate-spin" /> Transferring…</>
            ) : (
              <><ArrowRightLeft size={14} className="mr-1.5" /> Transfer Material</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TransferMaterialDialog;
